import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Input,
  Textarea,
  Select,
  VStack,
  HStack,
  Text,
  IconButton,
  Divider,
} from '@chakra-ui/react';
import { FaCopy } from 'react-icons/fa';
import { toast } from 'react-toastify';
import httpInjectorService from 'services/http-injector.service';
import ShareUserSelect from './ShareUserSelect';

const ShareWorkItemModal = ({
  isOpen,
  onClose,
  issue_id,
  issue_type_id,
  project_id,
  subtask_id,
  epic_id,
  // taskKey,
}) => {
  const [message, setMessage] = useState('');
  const [access, setAccess] = useState('USER');
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const assigneeList = useSelector(
    (state) => state?.Authentication?.assigneelist,
  );

  const issueLink = `${window.location.origin}/admin/taskBoard/${project_id}/taskDetails/${issue_id}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(issueLink);
    toast.success('Link copied to clipboard');
  };

  const handleShare = async () => {
    if (!selectedUsers.length) {
      toast.error('Select at least one user');
      return;
    }

    setLoading(true);

    try {
      // Build payload conditionally based on work item type
      const payload = {
        project_id,
        issue_type: issue_type_id,
        shared_with: selectedUsers.map((u) => u.label),
        access_level: access,
        message,
        work_item_url: issueLink,
      };

      // Only include the relevant ID based on type
      if (subtask_id) {
        payload.subtask_id = subtask_id;
        payload.issue_id = issue_id; // Parent issue
      } else if (epic_id) {
        payload.issue_id = issue_id; // Parent issue
        payload.epic_id = epic_id;
      } else {
        payload.issue_id = issue_id; // task_id(task_level/issue_level)
      }

      // POST shareWorkItem api call
      const res = await httpInjectorService.shareWorkItem(payload);

      if (res?.status === 'success') {
        toast.success(`Work item shared successfully`);
        setSelectedUsers([]);
        setMessage('');
        setAccess('USER');
        onClose();
      } else {
        toast.error(res?.message || 'Failed to share');
      }
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Share work item</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {/* Copy link */}
            <HStack>
              <Input value={issueLink} isReadOnly />
              <IconButton
                icon={<FaCopy />}
                onClick={handleCopyLink}
                aria-label="Copy link"
              />
            </HStack>

            <Divider />

            {/* Email / Username */}

            <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={0}>
              Names or emails
            </Text>

            <ShareUserSelect
              users={[]}
              value={selectedUsers}
              onChange={setSelectedUsers}
              assigneeList={assigneeList}
            />

            {/* Access */}
            <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={0}>
              Access level
            </Text>
            <Select value={access} onChange={(e) => setAccess(e.target.value)}>
              <option value="USER">User</option>
              <option value="ADMIN">Admin</option>
            </Select>

            {/* Message */}
            <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={0}>
              Message (optional)
            </Text>
            <Textarea
              placeholder="Anything they should know?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              resize="vertical"
            />

            <Text fontSize="sm" color="gray.500">
              The recipient will receive an email with access to this work item.
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleShare} isLoading={loading}>
            Share
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ShareWorkItemModal;
